import React, { useState, useEffect } from "react";
import { Menu, X, Phone } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="3562" className="hidden md:flex gap-6">
        {[1, 2, 3].map(i => <div key={i} data-index={i} className="h-5 w-20 bg-muted/20 animate-pulse rounded-sm" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-6">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative text-sm font-sans font-light font-semibold tracking-tight text-primary transition-colors hover:text-primary/80">
                <span data-index={i}>{item.label}</span>
                <span className="absolute -bottom-1 left-0 h-0.5 w-0 bg-primary transition-all duration-300 group-hover:w-full" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 p-4">
        {[1, 2, 3].map(i => <div key={i} data-index={i} className="h-8 w-full bg-muted/10 animate-pulse rounded-sm" />)}
      </div>;
  }
  return <nav className="flex flex-col gap-2 p-4">
      {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} className="block px-4 py-3 text-lg font-light font-bold uppercase tracking-tighter text-primary hover:bg-accent hover:text-primary/80 transition-colors" onClick={onClose}>
          <span data-index={i}>{item.label}</span>
        </Link>)}
    </nav>;
}
export default function Naglowek() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="header" className={cn("sticky top-0 z-50 w-full border-b border-transparent transition-all duration-300 font-sans", isScrolled ? "bg-white backdrop-blur-lg border-black/5 shadow-xl" : "bg-white")}>
      <div className="container mx-auto px-4 h-16 md:h-20 flex items-center justify-between">
        
        <div className="flex-shrink-0 flex items-center">
          <Link to="/" className="flex items-center gap-2">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1614558/img/image_tieolqse.png" alt="BH Marketing" className="!w-[180px] !h-auto" loading="lazy" />
          </Link>
        </div>

        
        <div className="hidden md:block">
          <MenuProvider menu_id="46">
            <HeaderMenuConsumer />
          </MenuProvider>
        </div>

        
        <div className="flex items-center gap-4">
          <Button asChild className="hidden md:inline-flex bg-white text-primary hover:bg-white/90 font-bold uppercase tracking-tight animate-[pulse_3s_ease-in-out_infinite] rounded-lg">
            <Link to="/ContactPage">
              <Phone className="mr-2 h-4 w-4" />
              Bezpłatna Konsultacja
            </Link>
          </Button>

          
          <Button variant="ghost" size="icon" className="md:hidden text-black hover:bg-black/5" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
            {isMobileMenuOpen ? <X className="h-6 w-6 text-black" /> : <Menu className="h-6 w-6 text-black" />}
          </Button>
        </div>
      </div>

      
      {isMobileMenuOpen && <div className="md:hidden absolute top-full left-0 w-full bg-white border-b border-border shadow-2xl animate-in slide-in-from-top-5 duration-300">
          <MenuProvider menu_id="46">
            <MobileMenuConsumer onClose={() => setIsMobileMenuOpen(false)} />
          </MenuProvider>
          <div className="p-4 pt-0">
            <Button asChild className="w-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg font-bold uppercase">
              <Link to="/ContactPage">
                <Phone className="mr-2 h-4 w-4" />
                Darmowa Konsultacja
              </Link>
            </Button>
          </div>
        </div>}
    </header>;
}